import { useEffect, useRef } from 'react';
import type { Params } from '../../lib/types';

interface Props {
  histogram: number[];
  params: Params;
  setParam: <K extends keyof Params>(k: K, v: Params[K]) => void;
}

export function ThresholdSlider({ histogram, params, setParam }: Props) {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const c = canvasRef.current;
    if (!c) return;
    const ctx = c.getContext('2d');
    if (!ctx) return;
    const max = histogram.reduce((a, b) => (b > a ? b : a), 1);
    ctx.clearRect(0, 0, c.width, c.height);
    ctx.fillStyle = getComputedStyle(document.documentElement).getPropertyValue('--tertiary').trim() || '#ccc';
    for (let i = 0; i < 256; i++) {
      const h = (histogram[i] / max) * c.height;
      ctx.fillRect((i / 256) * c.width, c.height - h, c.width / 256, h);
    }
  }, [histogram]);

  if (params.thresholdMethod === 'adaptive') return null;

  const isOtsu = params.thresholdMethod === 'otsu';
  const value = params.thresholdManual;

  return (
    <label className="block">
      <div className="mb-1 flex items-baseline justify-between text-sm">
        <span>Threshold {isOtsu && <em className="text-[var(--secondary)]">(Otsu auto)</em>}</span>
        <span className="font-mono">{value}</span>
      </div>
      <div className="relative h-12">
        <canvas ref={canvasRef} width={512} height={48} className="absolute inset-0 h-full w-full opacity-60" />
        <input
          type="range"
          min={0}
          max={255}
          value={value}
          disabled={isOtsu}
          onChange={(e) => setParam('thresholdManual', Number(e.target.value))}
          className="absolute inset-x-0 bottom-0 w-full"
          aria-label="Threshold"
        />
      </div>
    </label>
  );
}
